import { clean, firstString, isRecord, parseCurrency, parsePrice } from './product';

/**
 * The good case. Most real retail sites ship a schema.org `Product` block in
 * `application/ld+json` carrying name, image[], description and offers.price —
 * already clean, already structured, free, and instant.
 *
 * On a site that does this properly the rest of the extraction never runs.
 */

/** What a Product node is worth, once the schema.org shapes are unwrapped. */
export type JsonLdProduct = {
  title: string | undefined;
  description: string | undefined;
  price: number | undefined;
  currency: string | undefined;
  images: string[];
};

/**
 * Subtypes that carry the same fields. `@type` is also allowed to be an array
 * — a node can be both a Product and something else.
 */
const PRODUCT_TYPES = new Set(['Product', 'IndividualProduct', 'ProductModel', 'ProductGroup']);

export function productFromJsonLd(doc: Document): JsonLdProduct | undefined {
  const blocks = [...doc.querySelectorAll('script[type="application/ld+json"]')];

  const node = blocks
    .flatMap((block) => safeParse(block.textContent))
    .flatMap(flattenGraph)
    .find(isProduct);

  if (node === undefined) return undefined;

  const offer = firstOffer(node['offers']);

  return {
    title: firstString(node['name']),
    description: clean(node['description']),
    price: offer?.price,
    currency: offer?.currency,
    images: imageUrls(node['image']),
  };
}

/**
 * A malformed block is not a failed import — pages routinely ship one broken
 * script tag beside three good ones.
 */
function safeParse(text: string | null): unknown[] {
  if (text === null || text.trim() === '') return [];

  try {
    return [JSON.parse(text)];
  } catch {
    return [];
  }
}

/**
 * One block can be a node, an array of nodes, or a `@graph` envelope holding
 * the real ones. The envelope is kept as well as its contents — some sites put
 * the Product at the top level and the graph beside it.
 */
function flattenGraph(value: unknown): unknown[] {
  if (Array.isArray(value)) return value.flatMap(flattenGraph);
  if (!isRecord(value)) return [];

  const graph = value['@graph'];
  return graph === undefined ? [value] : [value, ...flattenGraph(graph)];
}

function isProduct(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value)) return false;

  const type = value['@type'];
  const names = Array.isArray(type) ? type : [type];
  return names.some((name) => typeof name === 'string' && PRODUCT_TYPES.has(name));
}

/**
 * `offers` is one Offer, an array of them, or an AggregateOffer wrapping a
 * range. The first one with an actual number wins — a list of sellers is
 * ordered with the page's own choice first.
 */
function firstOffer(
  offers: unknown,
): { price: number | undefined; currency: string | undefined } | undefined {
  const candidates = Array.isArray(offers) ? offers : [offers];

  for (const candidate of candidates) {
    if (!isRecord(candidate)) continue;

    const spec = isRecord(candidate['priceSpecification'])
      ? candidate['priceSpecification']
      : undefined;

    const price =
      parsePrice(candidate['price']) ??
      // AggregateOffer: a range. The bottom of it is the honest headline.
      parsePrice(candidate['lowPrice']) ??
      parsePrice(spec?.['price']);

    if (price === undefined) continue;

    return {
      price,
      currency:
        parseCurrency(candidate['priceCurrency']) ?? parseCurrency(spec?.['priceCurrency']),
    };
  }

  return undefined;
}

/** `image` is a string, an array, an ImageObject, or an array of those. */
function imageUrls(value: unknown): string[] {
  if (Array.isArray(value)) return value.flatMap(imageUrls);

  if (isRecord(value)) {
    const url = clean(value['url']) ?? clean(value['contentUrl']);
    return url === undefined ? [] : [url];
  }

  const url = clean(value);
  return url === undefined ? [] : [url];
}
